import { CONFIG, whatsappUrl } from "../../config";
import { ShieldCheck, MessageCircle } from "lucide-react";
import { useI18n } from "../../i18n";

const PLAY_BADGE = "https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg";
const APPLE_BADGE = "https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg";

export default function Footer() {
  const { t } = useI18n();
  return (
    <footer className="border-t border-[#23232a] bg-[#09090b] pb-24 lg:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: "linear-gradient(135deg,#f5d06a,#8a6a15)" }}>
              <ShieldCheck className="w-4 h-4 text-[#141006]" />
            </span>
            <div>
              <div className="font-display font-bold">{CONFIG.PRODUCT_NAME}</div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--ink-mute)]">{t("footer_product_tag")}</div>
            </div>
          </div>
          <p className="text-sm text-[color:var(--ink-dim)] mt-4 sm:mt-5 max-w-md leading-relaxed">{t("footer_about")}</p>

          <div className="mt-5 sm:mt-6">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-3">{t("footer_download")}</div>
            <div className="flex flex-wrap gap-3">
              <a href={CONFIG.PLAYSTORE_URL} target="_blank" rel="noreferrer" data-testid="footer-playstore"
                className="block transition-transform hover:scale-105">
                <img src={PLAY_BADGE} alt="Get it on Google Play" className="h-10 sm:h-12 w-auto" loading="lazy" />
              </a>
              <a href={CONFIG.APPSTORE_URL} target="_blank" rel="noreferrer" data-testid="footer-appstore"
                className="block transition-transform hover:scale-105">
                <img src={APPLE_BADGE} alt="Download on the App Store" className="h-10 sm:h-12 w-auto" loading="lazy" />
              </a>
            </div>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-3">{t("footer_product")}</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#features" className="text-[color:var(--ink-dim)] hover:text-[color:var(--gold)]">{t("nav_features")}</a></li>
            <li><a href="#dashboard" className="text-[color:var(--ink-dim)] hover:text-[color:var(--gold)]">{t("nav_dashboard")}</a></li>
            <li><a href="#pricing" className="text-[color:var(--ink-dim)] hover:text-[color:var(--gold)]">{t("nav_pricing")}</a></li>
            <li><a href="#faq" className="text-[color:var(--ink-dim)] hover:text-[color:var(--gold)]">{t("nav_faq")}</a></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-3">{t("footer_contact")}</div>
          <a href={whatsappUrl()} target="_blank" rel="noreferrer" data-testid="footer-whatsapp"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-[#23232a] hover:border-[color:var(--green)] text-sm">
            <MessageCircle className="w-4 h-4 text-[color:var(--green)]" /> {t("footer_setup")}
          </a>
          <div className="mt-4 text-xs text-[color:var(--ink-mute)]">{t("footer_sold_by", { seller: CONFIG.SELLER })}</div>
        </div>
      </div>

      <div className="border-t border-[#23232a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-5 gap-y-2 text-[12px]">
            <a href="/contact" data-testid="footer-contact-link" className="text-[color:var(--ink-dim)] hover:text-[color:var(--gold)] transition-colors">Contact</a>
            <a href="/terms" data-testid="footer-terms-link" className="text-[color:var(--ink-dim)] hover:text-[color:var(--gold)] transition-colors">Terms of Service</a>
            <a href="/privacy" data-testid="footer-privacy-link" className="text-[color:var(--ink-dim)] hover:text-[color:var(--gold)] transition-colors">Privacy Policy</a>
            <a href="/refund-cancellation" data-testid="footer-refund-link" className="text-[color:var(--ink-dim)] hover:text-[color:var(--gold)] transition-colors">Refund &amp; Cancellation</a>
            <a href="/my-orders" className="text-[color:var(--ink-dim)] hover:text-[color:var(--gold)] transition-colors">My orders</a>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="text-[11px] sm:text-xs text-[color:var(--ink-mute)]">{t("footer_copyright", { year: new Date().getFullYear() })}</div>
            <div className="text-[10px] sm:text-[11px] text-[color:var(--ink-mute)] max-w-2xl text-center sm:text-right">{t("footer_disclaimer")}</div>
          </div>
        </div>
      </div>
    </footer>
  );
}
